import type { IStorageProvider, StorageSnapshot } from '@/interfaces/storage';
import { BLOB_CHUNK_BYTES } from '@/utils/blob';

export type { StorageSnapshot } from '@/interfaces/storage';

const REVISION_PREFIX = '\0revision:';
const RETIRED_LEGACY_PREFIX = '\0retired-legacy:';
const MIGRATED_LEGACY_PREFIX = '\0migrated-legacy:';
// Project names are arbitrary UTF-16 strings. URI encoding rejects lone
// surrogates; preserve them exactly in the backup namespace instead. Literal
// percent signs still encode as %25, so these escapes cannot collide.
function backupName(name: string): string {
  let result = '';
  for (const character of name) {
    const code = character.charCodeAt(0);
    result +=
      character.length === 1 && code >= 0xd800 && code <= 0xdfff
        ? `%u${code.toString(16)}`
        : encodeURIComponent(character);
  }
  return result;
}
interface BlobMetadata {
  type: string;
  chunks: number;
}

interface ConversionSource {
  key: string;
  revision: number;
  legacy: string | null;
  present: boolean;
  original: unknown;
  localOnly: boolean;
}

export class StorageSnapshotConflict extends Error {
  constructor() {
    super(
      'This project changed in another window. Read and convert the latest source before saving.',
    );
    this.name = 'StorageSnapshotConflict';
  }
}

/** Project-sized records with transactional cross-tab updates and lazy legacy migration. */
export class IndexedDbStorageProvider implements IStorageProvider {
  readonly supportsBlobs = true;
  private database?: Promise<IDBDatabase>;
  private readonly channel?: BroadcastChannel;
  private readonly conversionSources = new WeakMap<StorageSnapshot, ConversionSource>();
  constructor(
    private readonly namespace: string,
    private readonly validateLegacy?: (key: string, value: unknown) => void,
    private readonly options: { preserveLegacyProjects?: boolean } = {},
  ) {
    if (typeof BroadcastChannel !== 'undefined')
      this.channel = new BroadcastChannel(`${namespace}:changes`);
  }

  private open(): Promise<IDBDatabase> {
    if (!this.database)
      this.database = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(this.namespace, 2);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains('records')) db.createObjectStore('records');
          if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
        };
        request.onerror = () => {
          this.database = undefined;
          reject(request.error);
        };
        request.onblocked = () => {
          this.database = undefined;
          reject(
            new Error('Close older Digitiles tabs and retry opening project storage.'),
          );
        };
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => {
            db.close();
            this.database = undefined;
          };
          resolve(db);
        };
      });
    return this.database;
  }

  private legacy(key: string): string | null {
    return localStorage.getItem(`${this.namespace}:${key}`);
  }
  private clearLegacy(key: string, original: string | null, explicitDelete = false) {
    // A current save must not discard legacy bytes retained by conversion,
    // including writes from older tabs that arrived after the reviewed commit.
    if (
      !explicitDelete &&
      this.options.preserveLegacyProjects &&
      key.startsWith('project:')
    )
      return;
    if (original !== null && this.legacy(key) === original)
      localStorage.removeItem(`${this.namespace}:${key}`);
  }
  private parseLegacy(key: string, raw: string) {
    const value: unknown = JSON.parse(raw);
    this.validateLegacy?.(key, value);
    return value;
  }
  private notify(key: string) {
    this.channel?.postMessage(key);
  }

  update<T>(key: string, change: (current: T | null) => T): Promise<T> {
    return this.updateAsync(key, async (current) => change(current));
  }

  private readSnapshot(
    db: IDBDatabase,
    key: string,
  ): Promise<{
    value: unknown;
    revision: number;
    retiredLegacy?: string;
    present: boolean;
  }> {
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['records', 'blobs'], 'readonly');
      const records = transaction.objectStore('records');
      const blobs = transaction.objectStore('blobs');
      const value = records.get(key),
        revision = records.get(REVISION_PREFIX + key);
      const recordKey = records.getKey(key);
      const retiredLegacy = records.get(RETIRED_LEGACY_PREFIX + key);
      const metadata = blobs.get(key);
      const chunks: IDBRequest<ArrayBuffer>[] = [];
      metadata.onsuccess = () => {
        const info = metadata.result as BlobMetadata | undefined;
        if (info)
          for (let index = 0; index < info.chunks; index++)
            chunks.push(blobs.get([key, index]));
      };
      transaction.oncomplete = () => {
        const info = metadata.result as BlobMetadata | undefined;
        if (
          info &&
          (value.result === undefined ||
            chunks.some((chunk) => chunk.result === undefined))
        ) {
          reject(new Error('Project storage is missing binary data.'));
          return;
        }
        resolve({
          value: info
            ? new Blob(
                chunks.map((chunk) => chunk.result),
                { type: info.type },
              )
            : value.result,
          revision: revision.result ?? 0,
          retiredLegacy: retiredLegacy.result,
          present: recordKey.result !== undefined,
        });
      };
      transaction.onabort = () => reject(transaction.error);
    });
  }

  private async encodeValue(value: unknown) {
    if (!(value instanceof Blob))
      return { value, metadata: undefined, chunks: [] as ArrayBuffer[] };
    // WebKit can reject even tiny native IDB Blob writes. Binary chunks also
    // bound each structured-clone transfer below browser record-size limits.
    // Read each slice directly: a single large native Blob read can fail even
    // when the same bytes are readable in bounded pieces (notably in WebKit).
    const chunks: ArrayBuffer[] = [];
    for (let start = 0; start < value.size; start += BLOB_CHUNK_BYTES)
      chunks.push(await value.slice(start, start + BLOB_CHUNK_BYTES).arrayBuffer());
    return { value: null, metadata: { type: value.type, chunks: chunks.length }, chunks };
  }

  private clearBlob(
    store: IDBObjectStore,
    key: string,
    metadata: BlobMetadata | undefined,
  ) {
    if (!metadata) return;
    store.delete(key);
    for (let index = 0; index < metadata.chunks; index++) store.delete([key, index]);
  }

  /** A converter/main-format probe must not migrate or remove old project data.
   * Keep the original private so mutating the returned DTO cannot alter a backup. */
  async readUnmigrated(key: string, legacyOnly = false): Promise<StorageSnapshot> {
    const db = await this.open(),
      legacy = this.legacy(key);
    const stored = legacyOnly
      ? {
          value: undefined,
          present: false,
          retiredLegacy: undefined,
          revision: await new Promise<number>((resolve, reject) => {
            const transaction = db.transaction('records', 'readonly');
            const revision = transaction
              .objectStore('records')
              .get(REVISION_PREFIX + key);
            transaction.oncomplete = () => resolve(revision.result ?? 0);
            transaction.onabort = () => reject(transaction.error);
          }),
        }
      : await this.readSnapshot(db, key);
    if (this.legacy(key) !== legacy) throw new StorageSnapshotConflict();
    if (
      !legacyOnly &&
      (!Number.isSafeInteger(stored.revision) ||
        stored.revision < 0 ||
        stored.revision >= Number.MAX_SAFE_INTEGER)
    )
      throw new Error(
        'Project storage has an invalid revision. The source has been preserved.',
      );
    const visibleLegacy = !legacyOnly && stored.retiredLegacy === legacy ? null : legacy;
    const fromIndexedDb = !legacyOnly && stored.present;
    const original = !fromIndexedDb
      ? visibleLegacy === null
        ? null
        : new Blob([visibleLegacy], { type: 'application/json' })
      : stored.value;
    const snapshot = Object.freeze({
      key,
      value: original instanceof Blob ? original : structuredClone(original),
      source: fromIndexedDb
        ? ('indexeddb' as const)
        : visibleLegacy !== null
          ? ('localStorage' as const)
          : ('missing' as const),
    });
    this.conversionSources.set(snapshot, {
      key,
      revision: stored.revision,
      legacy,
      present: fromIndexedDb || visibleLegacy !== null,
      original,
      localOnly: !fromIndexedDb && visibleLegacy !== null,
    });
    return snapshot;
  }

  /** Commit precisely the reviewed source, without retrying on a newer revision.
   * Conversion/validation is the caller's responsibility. Backups and replacement
   * share one IDB transaction; abort/quota failures preserve both source stores. */
  async compareAndSwap(
    snapshot: StorageSnapshot,
    value: Blob,
    signal?: AbortSignal,
    destinationName?: string,
  ) {
    return this.commitConvertedProject(snapshot, value, signal, destinationName);
  }

  /** Automatic migration keeps the public name and retains both a transactional
   * backup and any localStorage source, including late writes from older tabs. */
  async migrateProject(snapshot: StorageSnapshot, value: Blob, signal?: AbortSignal) {
    return this.commitConvertedProject(snapshot, value, signal, undefined, true);
  }

  private async commitConvertedProject(
    snapshot: StorageSnapshot,
    value: Blob,
    signal?: AbortSignal,
    destinationName?: string,
    automatic = false,
  ): Promise<{
    backupKey: string;
    projectName: string;
    legacySourceChanged: boolean;
    legacyDestinationChanged: boolean;
  }> {
    const source = this.conversionSources.get(snapshot);
    if (!source || !source.present || !source.key.startsWith('project:'))
      throw new Error(
        'Read a saved project with this storage provider before converting it.',
      );
    if (!(value instanceof Blob))
      throw new TypeError('A converted project must be a binary file.');
    if (
      !Number.isSafeInteger(source.revision) ||
      source.revision < 0 ||
      source.revision >= Number.MAX_SAFE_INTEGER
    )
      throw new Error(
        'Project storage has an invalid revision. Download the retained original for recovery.',
      );
    const projectName = destinationName ?? source.key.slice(8),
      destination = `project:${projectName}`;
    if (
      destinationName !== undefined &&
      (!projectName.trim() || projectName !== projectName.trim())
    )
      throw new Error('Enter a project name without surrounding spaces.');
    if (source.localOnly && destination === source.key && !automatic)
      throw new Error(
        'A localStorage project must be converted into a named copy to preserve changes from older windows.',
      );
    if (!source.localOnly && destination !== source.key)
      throw new Error(
        'An IndexedDB conversion must commit to its reviewed project record.',
      );
    const aborted = () =>
      new DOMException('Conversion cancelled. Your original is preserved.', 'AbortError');
    if (signal?.aborted) throw aborted();
    const db = await this.open();
    const encoded = await this.encodeValue(value);
    if (signal?.aborted) throw aborted();
    const backup = await this.encodeValue(source.original);
    if (signal?.aborted) throw aborted();
    const backupKey = `legacy-conversion-backup:${backupName(source.key.slice(8))}:${crypto.randomUUID()}`;
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(['records', 'blobs'], 'readwrite');
      const records = transaction.objectStore('records'),
        blobs = transaction.objectStore('blobs');
      const previousBlob = blobs.get(source.key),
        revision = records.get(REVISION_PREFIX + source.key);
      // Never overwrite a recovery copy, even if an ID generator is replaced.
      const existingBackup = records.get(backupKey),
        existingBackupBlob = blobs.get(backupKey);
      const target = destination !== source.key ? records.get(destination) : undefined;
      const targetBlob = destination !== source.key ? blobs.get(destination) : undefined;
      let failure: unknown,
        written = false;
      const abort = () => {
        failure = aborted();
        try {
          transaction.abort();
        } catch (error) {
          // A physically completed commit can precede its oncomplete event.
          // Let that event report success instead of claiming it was undone.
          if (!(error instanceof DOMException && error.name === 'InvalidStateError'))
            throw error;
        }
      };
      signal?.addEventListener('abort', abort, { once: true });
      const put = (key: string, data: typeof encoded) => {
        records.put(data.value, key);
        if (data.metadata) {
          blobs.put(data.metadata, key);
          data.chunks.forEach((chunk, index) => blobs.put(chunk, [key, index]));
        }
      };
      const checked = targetBlob ?? existingBackupBlob;
      checked.onsuccess = () => {
        try {
          if (signal?.aborted) throw aborted();
          if (this.conversionSources.get(snapshot) !== source)
            throw new Error(
              'This conversion source was already committed. Read it again.',
            );
          if (
            (revision.result ?? 0) !== source.revision ||
            this.legacy(source.key) !== source.legacy
          )
            throw new StorageSnapshotConflict();
          if (
            existingBackup.result !== undefined ||
            existingBackupBlob.result !== undefined
          )
            throw new Error(
              'This recovery key already exists. Read the source again before saving.',
            );
          if (
            target &&
            (target.result !== undefined ||
              targetBlob!.result !== undefined ||
              this.legacy(destination) !== null)
          )
            throw new Error(
              'A project already uses that name. Choose a different name and convert again.',
            );
          put(backupKey, backup);
          if (destination === source.key)
            this.clearBlob(blobs, source.key, previousBlob.result);
          put(destination, encoded);
          if (automatic && source.localOnly && source.legacy !== null)
            records.put(source.legacy, MIGRATED_LEGACY_PREFIX + destination);
          if (destination === source.key)
            records.put(source.revision + 1, REVISION_PREFIX + source.key);
          else {
            const targetRevision = records.get(REVISION_PREFIX + destination);
            targetRevision.onsuccess = () => {
              const currentRevision = targetRevision.result ?? 0;
              if (
                !Number.isSafeInteger(currentRevision) ||
                currentRevision < 0 ||
                currentRevision >= Number.MAX_SAFE_INTEGER
              ) {
                failure = new Error(
                  'The copy destination has an invalid revision. Your original is preserved.',
                );
                transaction.abort();
                return;
              }
              records.put(currentRevision + 1, REVISION_PREFIX + destination);
            };
          }
          written = true;
        } catch (error) {
          failure = error;
          transaction.abort();
        }
      };
      transaction.oncomplete = () => {
        signal?.removeEventListener('abort', abort);
        if (!written) {
          reject(new StorageSnapshotConflict());
          return;
        }
        this.conversionSources.delete(snapshot);
        resolve();
      };
      transaction.onabort = () => {
        signal?.removeEventListener('abort', abort);
        reject(
          failure ??
            transaction.error ??
            new Error('Conversion storage update was aborted.'),
        );
      };
      transaction.onerror = () => {};
      if (signal?.aborted) abort();
    });
    // Never delete localStorage here. Automatic migration shadows it with current
    // IDB bytes; racing legacy writes remain available through readUnmigrated(true).
    // The exact reviewed original and replacement share one IDB transaction.
    try {
      this.notify(destination);
    } catch {
      /* Notification cannot undo a durable commit. */
    }
    let legacySourceChanged = true,
      legacyDestinationChanged = destination !== source.key;
    try {
      legacySourceChanged = this.legacy(source.key) !== source.legacy;
      legacyDestinationChanged =
        destination !== source.key && this.legacy(destination) !== null;
    } catch {
      /* Access failure after commit is a warning, never a failed save. */
    }
    return { backupKey, projectName, legacySourceChanged, legacyDestinationChanged };
  }

  /** Prepare outside the transaction; atomically retry if another writer changed the record. */
  async updateAsync<T>(
    key: string,
    change: (current: T | null) => Promise<T>,
  ): Promise<T> {
    const db = await this.open();
    for (let attempt = 0; attempt < 32; attempt++) {
      const legacy = this.legacy(key);
      const snapshot = await this.readSnapshot(db, key);
      // Separate IDB deserialization from validation/computation and the final write.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const current = !snapshot.present
        ? legacy === null || snapshot.retiredLegacy === legacy
          ? null
          : (this.parseLegacy(key, legacy) as T)
        : (snapshot.value as T);
      const value = await change(current);
      const encoded = await this.encodeValue(value);
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      const committed = await new Promise<boolean>((resolve, reject) => {
        const transaction = db.transaction(['records', 'blobs'], 'readwrite'),
          store = transaction.objectStore('records');
        const blobs = transaction.objectStore('blobs');
        const previousBlob = blobs.get(key);
        const revision = store.get(REVISION_PREFIX + key);
        let written = false,
          failure: unknown;
        revision.onsuccess = () => {
          if (
            (revision.result ?? 0) !== snapshot.revision ||
            (!snapshot.present && this.legacy(key) !== legacy)
          )
            return;
          try {
            this.clearBlob(blobs, key, previousBlob.result);
            store.put(encoded.value, key);
            if (encoded.metadata) {
              blobs.put(encoded.metadata, key);
              encoded.chunks.forEach((chunk, index) => blobs.put(chunk, [key, index]));
            }
            store.put(snapshot.revision + 1, REVISION_PREFIX + key);
            written = true;
          } catch (error) {
            failure = error;
            transaction.abort();
          }
        };
        transaction.oncomplete = () => resolve(written);
        transaction.onabort = () =>
          reject(
            failure ??
              transaction.error ??
              new Error('Project storage update was aborted.'),
          );
        transaction.onerror = () => {};
      });
      if (!committed) continue;
      this.clearLegacy(key, legacy);
      this.notify(key);
      return value;
    }
    throw new Error(
      'This project kept changing in another window. Retry saving your work.',
    );
  }

  async save(key: string, value: unknown): Promise<void> {
    await this.update(key, () => value);
  }

  async load<T>(key: string): Promise<T | null> {
    const db = await this.open();
    const snapshot = await this.readSnapshot(db, key);
    const current = snapshot.value as T | undefined;
    if (snapshot.present) return current as T;
    if (this.legacy(key) === null || this.legacy(key) === snapshot.retiredLegacy)
      return null;
    // Recheck inside a write transaction so another tab's newer value always wins.
    return this.update<T | null>(key, (value) => value);
  }

  async moveProject(
    snapshot: StorageSnapshot,
    destination: string,
    value: Blob,
  ): Promise<void> {
    const source = this.conversionSources.get(snapshot);
    if (
      !source ||
      snapshot.source !== 'indexeddb' ||
      !source.key.startsWith('project:') ||
      !destination.startsWith('project:')
    )
      throw Error('Reload this project before renaming it.');
    const encoded = await this.encodeValue(value);
    const db = await this.open();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(['records', 'blobs'], 'readwrite');
      const records = tx.objectStore('records'),
        blobs = tx.objectStore('blobs');
      const sourceBlob = blobs.get(source.key),
        targetBlob = blobs.get(destination);
      const revision = records.get(REVISION_PREFIX + source.key);
      const targetRevision = records.get(REVISION_PREFIX + destination);
      const target = records.getKey(destination);
      const retired = records.get(RETIRED_LEGACY_PREFIX + destination);
      const migrated = records.get(MIGRATED_LEGACY_PREFIX + source.key);
      let failure: unknown;
      migrated.onsuccess = () => {
        try {
          if ((revision.result ?? 0) !== source.revision)
            throw Error('This project changed in another window. Retry renaming it.');
          const legacy = this.legacy(destination);
          if (
            destination !== source.key &&
            (target.result !== undefined ||
              (legacy !== null && legacy !== retired.result))
          )
            throw Error('A project with this name already exists.');
          this.clearBlob(blobs, destination, targetBlob.result);
          records.put(encoded.value, destination);
          if (encoded.metadata) {
            blobs.put(encoded.metadata, destination);
            encoded.chunks.forEach((chunk, index) =>
              blobs.put(chunk, [destination, index]),
            );
          }
          records.put((targetRevision.result ?? 0) + 1, REVISION_PREFIX + destination);
          if (destination !== source.key) {
            this.clearBlob(blobs, source.key, sourceBlob.result);
            records.delete(source.key);
            records.put(source.revision + 1, REVISION_PREFIX + source.key);
            // Preserve retained legacy originals, matching ordinary project rename.
            if (typeof migrated.result === 'string')
              records.put(migrated.result, RETIRED_LEGACY_PREFIX + source.key);
          }
        } catch (error) {
          failure = error;
          tx.abort();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onabort = () =>
        reject(failure ?? tx.error ?? Error('Project rename was aborted.'));
      tx.onerror = () => {};
    });
    try {
      this.notify(source.key);
      this.notify(destination);
    } catch {
      /* The atomic move is already committed. */
    }
  }

  async delete(key: string, options?: { preserveLegacy?: boolean }): Promise<void> {
    const db = await this.open(),
      legacy = options?.preserveLegacy ? null : this.legacy(key);
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction(['records', 'blobs'], 'readwrite');
      const store = transaction.objectStore('records');
      const blobs = transaction.objectStore('blobs');
      const previousBlob = blobs.get(key);
      previousBlob.onsuccess = () => this.clearBlob(blobs, key, previousBlob.result);
      const revision = store.get(REVISION_PREFIX + key);
      revision.onsuccess = () =>
        store.put((revision.result ?? 0) + 1, REVISION_PREFIX + key);
      store.delete(key);
      if (options?.preserveLegacy && key.startsWith('project:')) {
        // Retire only the original that automatic migration actually reviewed.
        // A late old-tab write has different bytes and remains visible/recoverable.
        const migrated = store.get(MIGRATED_LEGACY_PREFIX + key);
        migrated.onsuccess = () => {
          if (typeof migrated.result === 'string')
            store.put(migrated.result, RETIRED_LEGACY_PREFIX + key);
        };
      } else {
        store.delete(RETIRED_LEGACY_PREFIX + key);
        store.delete(MIGRATED_LEGACY_PREFIX + key);
      }
      transaction.oncomplete = () => resolve();
      transaction.onabort = () => reject(transaction.error);
    });
    if (!options?.preserveLegacy) this.clearLegacy(key, legacy, true);
    this.notify(key);
  }

  async listKeys(prefix: string): Promise<string[]> {
    const db = await this.open();
    const keys = await new Promise<IDBValidKey[]>((resolve, reject) => {
      const request = db
        .transaction('records', 'readonly')
        .objectStore('records')
        .getAllKeys();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
    const result = new Set(
      keys.filter(
        (key): key is string =>
          typeof key === 'string' &&
          !key.startsWith(REVISION_PREFIX) &&
          !key.startsWith(RETIRED_LEGACY_PREFIX) &&
          !key.startsWith(MIGRATED_LEGACY_PREFIX) &&
          key.startsWith(prefix),
      ),
    );
    const legacyPrefix = `${this.namespace}:${prefix}`;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(legacyPrefix)) {
        const projectKey = key.slice(this.namespace.length + 1);
        if (result.has(projectKey)) continue;
        const snapshot = await this.readSnapshot(db, projectKey);
        if (this.legacy(projectKey) !== snapshot.retiredLegacy) result.add(projectKey);
      }
    }
    return [...result];
  }

  subscribe(key: string, onChange: () => void): () => void {
    const message = (event: MessageEvent) => {
      if (event.data === key) onChange();
    };
    const storage = (event: StorageEvent) => {
      if (
        event.storageArea === localStorage &&
        (event.key === null || event.key === `${this.namespace}:${key}`)
      )
        onChange();
    };
    this.channel?.addEventListener('message', message);
    window.addEventListener('storage', storage);
    return () => {
      this.channel?.removeEventListener('message', message);
      window.removeEventListener('storage', storage);
    };
  }
}
